import { createRef, useContext, useMemo } from 'react';
import Dropzone from 'react-dropzone';
import ToastContext from '../store/toast-context';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import { colors } from 'src/styles/theme';

const baseStyle = {
  flex: 1,
  display: 'flex',
  height: '120px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 1,
  borderRadius: '4px',
  borderColor: colors.dropzone.border,
  borderStyle: 'dashed',
  backgroundColor: colors.dropzone.background,
  color: colors.grey[500],
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle = {
  borderColor: colors.dropzone.focus,
};

const acceptStyle = {
  borderColor: colors.success.main,
};

const rejectStyle = {
  borderColor: colors.error.reject,
};

const acceptedFileTypes = {
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

export const Upload = ({
  onDrop = () => {},
  setError = () => {},
  name = 'file',
  loaded = true,
  disabled = false,
}) => {
  const { handleError, error } = useContext(ToastContext);

  const createInvalidFileTypeMsg = () => {
    const fileExts = Object.keys(acceptedFileTypes).reduce((pv, cv) => {
      return [...pv, ...acceptedFileTypes[cv]];
    }, []);

    if (fileExts.length <= 1) {
      return `File type must be ${fileExts.join('')} `;
    }

    if (fileExts.length === 2) {
      return `File type must be ${fileExts.join(' or ')} `;
    }

    return `File type must be one of the following: ${fileExts.join(', ')}`;
  };

  const displayToastErrorsForFiles = (files) => {
    for (const { errors } of files) {
      setError(true);
      if (errors[0].code === 'file-invalid-type') {
        const msg = createInvalidFileTypeMsg();
        error(msg);
      } else {
        error(errors[0].message);
      }
    }
  };

  const dropzoneRef = createRef();

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Dropzone
        disabled={disabled}
        onDrop={onDrop}
        onDropRejected={displayToastErrorsForFiles}
        onError={handleError}
        maxSize={10000000} // 10 mb
        accept={acceptedFileTypes}
        ref={dropzoneRef}
      >
        {({
          getRootProps,
          getInputProps,
          isDragActive,
          isFileDialogActive,
          isDragAccept,
          isDragReject,
        }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const style = useMemo(
            () => ({
              ...baseStyle,
              ...(isDragActive || isFileDialogActive ? focusedStyle : {}),
              ...(isDragAccept ? acceptStyle : {}),
              ...(isDragReject ? rejectStyle : {}),
            }),
            [isDragActive, isFileDialogActive, isDragAccept, isDragReject]
          );
          return (
            <Box
              {...getRootProps({ style })}
              sx={{
                ...style,
                width: '100%',
                cursor: 'pointer',
                gap: '8px',
              }}
            >
              <input data-cy="file-input" {...getInputProps()} name={name} />
              {loaded ? (
                <>
                  <Box
                    component="span"
                    sx={{
                      color: colors.secondary.main,
                      textDecoration: 'underline',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      lineHeight: '20px',
                    }}
                  >
                    Upload
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: colors.grey[500],
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      lineHeight: '20px',
                    }}
                  >
                    (.XLS file only)
                  </Box>
                </>
              ) : (
                <CircularProgress />
              )}
            </Box>
          );
        }}
      </Dropzone>
    </Box>
  );
};
